import { useQueryClient } from '@tanstack/react-query';

import { Contract, createDataCacheKeys } from '~/data';
import {
  ContractFilter,
  ContractOrderBy,
  useContractApi,
  UsePaginatedQueryResult,
  useSuspensePaginatedQuery,
} from '~/data/api';

export const cacheKeys = createDataCacheKeys('contracts');

export type ContractOptions = {
  orderBy?: ContractOrderBy;
  filterBy?: ContractFilter;
  size?: number;
};

export type UseContractsResult = UsePaginatedQueryResult<Contract>;

export const useUpdateContractsCache = () => {
  const queryClient = useQueryClient();

  const updateContractsCache = async () => {
    await queryClient.refetchQueries({ queryKey: [...cacheKeys.list()] });
  };

  return { updateContractsCache };
};

export const useContracts = (
  { orderBy, filterBy, size }: ContractOptions = { filterBy: undefined, orderBy: '-start_date' }
): UseContractsResult => {
  const api = useContractApi();

  const { data, isLoading, isError, isSuccess, error, fetchNextPage } = useSuspensePaginatedQuery<Contract>({
    cacheKey: [...cacheKeys.list({ filterBy, orderBy })],
    fetchAll: (pageParams) =>
      api.getAll({ filter: filterBy, orderBy, page: { page: pageParams.page, size: size || pageParams.size } }),
  });

  return {
    data,
    error,
    fetchNextPage,
    isError,
    isLoading,
    isSuccess,
  };
};
